import { useState, type FormEvent, type KeyboardEvent } from 'react'
import { ArrowLeft, ArrowRight, CalendarDays, CheckCircle2, KeyRound, Loader2, Mail, MapPin, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router'
import { EVENT } from '../config/event'
import '../styles/login-concepts-v2.css'

type LoginConceptV2Variant = 'wayfinder' | 'ticket' | 'pulse' | 'brutalist' | 'study'
type Stage = 'email' | 'code' | 'password' | 'success'

interface ConceptSpec {
  number: string
  name: string
  label: string
  eyebrow: string
  headline: string
  accentWord: string
  description: string
  formTitle: string
  formSubtitle: string
  note: string
}

const CONCEPTS: Record<LoginConceptV2Variant, ConceptSpec> = {
  wayfinder: {
    number: '06',
    name: 'Wayfinder',
    label: 'Arrival map',
    eyebrow: 'UiTM / CPD PARTICIPANT ROUTE',
    headline: 'Your next useful direction starts here.',
    accentWord: 'useful direction',
    description: 'A wayfinding-led entrance that treats sign-in as the first clear marker on the journey through the programme.',
    formTitle: 'Find your programme',
    formSubtitle: 'Start with the email used for registration.',
    note: 'Wayfinding system · sky blue · route markers',
  },
  ticket: {
    number: '07',
    name: 'Field Pass',
    label: 'Your event pass',
    eyebrow: 'CPD 2026 / DELEGATE SERVICES',
    headline: 'Keep learning close at hand.',
    accentWord: 'learning close',
    description: 'A tangible pass-inspired treatment that makes access feel like part of arriving, not a barrier before the event.',
    formTitle: 'Open your pass',
    formSubtitle: 'Your registered email is the only thing we need.',
    note: 'Paper pass · ink black · red registration mark',
  },
  pulse: {
    number: '08',
    name: 'Clinical Pulse',
    label: 'A steady signal',
    eyebrow: 'PUSAT KESIHATAN UiTM / PROFESSIONAL DEVELOPMENT',
    headline: 'A clearer rhythm for better care.',
    accentWord: 'better care',
    description: 'A clinical interface with calm spacing and a signal motif, designed to feel capable without turning the login into a dashboard.',
    formTitle: 'Continue securely',
    formSubtitle: 'We’ll take you to the right sign-in step.',
    note: 'Clinical clarity · white space · measured red',
  },
  brutalist: {
    number: '09',
    name: 'Notice / Action',
    label: 'The registration notice',
    eyebrow: 'CPD 2026 / IMPORTANT: PARTICIPANT ACCESS',
    headline: 'SIGN IN. SHOW UP. KEEP LEARNING.',
    accentWord: 'KEEP LEARNING.',
    description: 'A direct, high-signal option for a busy registration desk: fewer decorative cues, stronger labels, unmistakable action.',
    formTitle: 'Sign in now',
    formSubtitle: 'Email first. We’ll route the next step.',
    note: 'Hard edges · highlighter yellow · plain language',
  },
  study: {
    number: '10',
    name: 'Night Study',
    label: 'After the last session',
    eyebrow: 'CONTINUING PROFESSIONAL DEVELOPMENT / 2026',
    headline: 'Make the next session count.',
    accentWord: 'next session',
    description: 'A focused evening treatment for delegates checking the programme, attendance and certificate status between sessions.',
    formTitle: 'Return to your event',
    formSubtitle: 'Use your registration email to continue.',
    note: 'Indigo study hall · electric blue · quiet focus',
  },
}

const CONCEPT_ROUTES: Array<{ variant: LoginConceptV2Variant; path: string }> = [
  { variant: 'wayfinder', path: '/preview/login/v2/wayfinder' },
  { variant: 'ticket', path: '/preview/login/v2/ticket' },
  { variant: 'pulse', path: '/preview/login/v2/pulse' },
  { variant: 'brutalist', path: '/preview/login/v2/brutalist' },
  { variant: 'study', path: '/preview/login/v2/study' },
]

export function getLoginConceptV2FromPath(pathname: string): LoginConceptV2Variant | null {
  return CONCEPT_ROUTES.find((route) => route.path === pathname)?.variant ?? null
}

export function LoginConceptsV2Route({ variant }: { variant: LoginConceptV2Variant }) {
  const spec = CONCEPTS[variant]
  const [stage, setStage] = useState<Stage>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState<string[]>(['', '', '', '', '', ''])
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  function submitEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!email.trim() || busy) return
    setBusy(true)
    window.setTimeout(() => {
      setBusy(false)
      setStage(email.toLowerCase().includes('staff') || email.toLowerCase().includes('admin') ? 'password' : 'code')
    }, 420)
  }

  function submitCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (code.join('').length !== 6 || busy) return
    setBusy(true)
    window.setTimeout(() => {
      setBusy(false)
      setStage('success')
    }, 520)
  }

  function submitPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!password.trim() || busy) return
    setBusy(true)
    window.setTimeout(() => {
      setBusy(false)
      setStage('success')
    }, 520)
  }

  function restart() {
    setStage('email')
    setCode(['', '', '', '', '', ''])
    setPassword('')
  }

  function updateDigit(index: number, rawValue: string) {
    const digits = rawValue.replace(/\D/g, '').slice(0, 6)
    const next = [...code]
    if (!digits) {
      next[index] = ''
      setCode(next)
      return
    }
    digits.split('').forEach((digit, offset) => {
      if (index + offset < next.length) next[index + offset] = digit
    })
    setCode(next)
    document.getElementById(`concept-v2-otp-${Math.min(index + digits.length, 5)}`)?.focus()
  }

  function handleDigitKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`concept-v2-otp-${index - 1}`)?.focus()
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      document.getElementById(`concept-v2-otp-${index - 1}`)?.focus()
    }
    if (event.key === 'ArrowRight' && index < 5) {
      document.getElementById(`concept-v2-otp-${index + 1}`)?.focus()
    }
  }

  const heading = stage === 'email' ? spec.formTitle : stage === 'code' ? 'Check your inbox' : stage === 'password' ? 'Staff access' : 'You’re signed in'
  const subtitle = stage === 'email' ? spec.formSubtitle : stage === 'code' ? 'Enter the six-digit code we sent you.' : stage === 'password' ? 'Enter the password for this venue account.' : 'Your conference dashboard is ready.'

  return (
    <main className={`lc2 lc2--${variant}`}>
      <div className="lc2__texture" aria-hidden="true" />

      <header className="lc2__topbar">
        <Link className="lc2__brand" to="/login" aria-label="CPD 2026 live sign-in">
          <span className="lc2__brand-mark">CPD</span>
          <span className="lc2__brand-year">2026</span>
        </Link>
        <div className="lc2__mode">FRESH CONCEPTS / {spec.number}</div>
        <Link className="lc2__live-link" to="/login">Live sign-in <ArrowRight size={14} aria-hidden="true" /></Link>
      </header>

      <nav className="lc2__switcher" aria-label="Fresh login concepts">
        <span className="lc2__switcher-label">Set two</span>
        {CONCEPT_ROUTES.map((route) => (
          <Link
            key={route.variant}
            className={route.variant === variant ? 'lc2__switcher-link lc2__switcher-link--active' : 'lc2__switcher-link'}
            to={route.path}
          >
            <span className="lc2__switcher-number">{CONCEPTS[route.variant].number}</span>
            <span>{CONCEPTS[route.variant].name}</span>
          </Link>
        ))}
      </nav>

      <div className="lc2__layout">
        <section className="lc2__story" aria-labelledby="concept-v2-headline">
          <div className="lc2__story-topline">
            <span className="lc2__index">{spec.number}</span>
            <span>{spec.eyebrow}</span>
          </div>

          <div className="lc2__story-copy">
            <p className="lc2__event-mark">{spec.label} <span aria-hidden="true">↗</span></p>
            <h1 id="concept-v2-headline" className="lc2__headline">
              {spec.headline.split(spec.accentWord)[0]}
              <em>{spec.accentWord}</em>
              {spec.headline.split(spec.accentWord)[1]}
            </h1>
            <p className="lc2__description">{spec.description}</p>
          </div>

          <dl className="lc2__event-meta">
            <div>
              <dt><CalendarDays size={15} aria-hidden="true" /> Dates</dt>
              <dd>{EVENT.dateLabel}</dd>
            </div>
            <div>
              <dt><MapPin size={15} aria-hidden="true" /> Venue</dt>
              <dd>{EVENT.venue}</dd>
            </div>
          </dl>

          {variant === 'wayfinder' ? (
            <div className="lc2__waypoints" aria-hidden="true">
              <span className="lc2__waypoint lc2__waypoint--active">01 <b>arrive</b></span>
              <span className="lc2__waypoint">02 <b>attend</b></span>
              <span className="lc2__waypoint">03 <b>claim</b></span>
            </div>
          ) : null}
          {variant === 'ticket' ? <div className="lc2__ticket-stamp" aria-hidden="true">FIELD<br />PASS</div> : null}
          {variant === 'pulse' ? <div className="lc2__pulse" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /></div> : null}
          {variant === 'brutalist' ? <div className="lc2__notice" aria-hidden="true">OPEN<br />ACCESS<br /><span>→</span></div> : null}
          {variant === 'study' ? <div className="lc2__study-note" aria-hidden="true"><span>Tonight’s note</span><b>Small steps<br />compound.</b></div> : null}

          <p className="lc2__design-note">{spec.note}</p>
        </section>

        <section className="lc2__panel" aria-label="Preview sign in">
          <div className="lc2__panel-heading" aria-live="polite">
            <p className="lc2__panel-kicker">{stage === 'success' ? 'ACCESS GRANTED' : 'PARTICIPANT ACCESS'}</p>
            <h2>{heading}</h2>
            <p>{subtitle}</p>
          </div>

          {stage === 'email' ? (
            <form className="lc2__form" onSubmit={submitEmail}>
              <label className="lc2__field-label" htmlFor="concept-v2-email">Email address</label>
              <div className="lc2__input-wrap">
                <Mail size={18} aria-hidden="true" />
                <input
                  id="concept-v2-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="name@uitm.edu.my"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>
              <p className="lc2__hint">We’ll identify whether this address uses a one-time code or staff password.</p>
              <button className="lc2__primary" type="submit" disabled={busy || !email.trim()}>
                {busy ? <Loader2 className="lc2__spin" size={18} aria-label="Checking" /> : <>Continue <ArrowRight size={17} aria-hidden="true" /></>}
              </button>
            </form>
          ) : null}

          {stage === 'code' ? (
            <form className="lc2__form" onSubmit={submitCode}>
              <div className="lc2__addressed">
                <span>Code sent to</span>
                <strong>{email.trim()}</strong>
                <button type="button" onClick={restart}>Change</button>
              </div>
              <fieldset className="lc2__otp-fieldset">
                <legend>Six-digit verification code</legend>
                <div className="lc2__otp-row">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      id={`concept-v2-otp-${index}`}
                      className="lc2__otp"
                      aria-label={`Digit ${index + 1} of 6`}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={digit}
                      onChange={(event) => updateDigit(index, event.target.value)}
                      onKeyDown={(event) => handleDigitKeyDown(index, event)}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>
              </fieldset>
              <p className="lc2__hint">For this visual preview, any six digits complete the sign-in.</p>
              <button className="lc2__primary" type="submit" disabled={busy || code.join('').length !== 6}>
                {busy ? <Loader2 className="lc2__spin" size={18} aria-label="Verifying" /> : <>Verify and continue <ArrowRight size={17} aria-hidden="true" /></>}
              </button>
              <button className="lc2__back" type="button" onClick={restart}><ArrowLeft size={14} aria-hidden="true" /> Use a different address</button>
            </form>
          ) : null}

          {stage === 'password' ? (
            <form className="lc2__form" onSubmit={submitPassword}>
              <div className="lc2__addressed">
                <span>Staff account</span>
                <strong>{email.trim()}</strong>
                <button type="button" onClick={restart}>Change</button>
              </div>
              <label className="lc2__field-label" htmlFor="concept-v2-password">Password</label>
              <div className="lc2__input-wrap">
                <KeyRound size={18} aria-hidden="true" />
                <input
                  id="concept-v2-password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Enter staff password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoFocus
                  required
                />
              </div>
              <button className="lc2__primary" type="submit" disabled={busy || !password.trim()}>
                {busy ? <Loader2 className="lc2__spin" size={18} aria-label="Signing in" /> : <>Sign in <ArrowRight size={17} aria-hidden="true" /></>}
              </button>
              <button className="lc2__back" type="button" onClick={restart}><ArrowLeft size={14} aria-hidden="true" /> Use a different address</button>
            </form>
          ) : null}

          {stage === 'success' ? (
            <div className="lc2__success">
              <CheckCircle2 size={46} aria-hidden="true" />
              <p className="lc2__success-title">Signed in successfully</p>
              <p>Your programme, attendance and certificate status are ready.</p>
              <button className="lc2__primary" type="button" onClick={restart}>Preview again <ArrowRight size={17} aria-hidden="true" /></button>
            </div>
          ) : null}

          <footer className="lc2__panel-footer">
            <ShieldCheck size={15} aria-hidden="true" /> Secure, encrypted login
          </footer>
        </section>
      </div>

      <footer className="lc2__footer">
        <span>Set two · design comparison only · no real sign-in occurs here</span>
        <Link to="/login">Return to live CPD sign-in</Link>
      </footer>
    </main>
  )
}
